import React, { lazy } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import AccountLayout from 'components/standard/AccountLayout';
import { roles } from 'utility/constants/roles';
import useBrandPolicyLinks from 'utility/hooks/useBrandPolicyLinks';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';

const AccountDetails = lazy(
	() => import('containers/pages/account/AccountDetails'),
);
const Activity = lazy(() => import('containers/pages/account/Activity'));
const APITokens = lazy(() => import('containers/pages/account/APITokens'));
const BillingOverview = lazy(
	() => import('containers/pages/account/BillingOverview'),
);
const ConfirmOneTimePayPalPayment = lazy(
	() => import('containers/pages/account/ConfirmOneTimePayPalPayment'),
);
const Transactions = lazy(
	() => import('containers/pages/account/Transactions'),
);
const Preferences = lazy(() => import('containers/pages/account/Preferences'));
const Users = lazy(() => import('containers/pages/account/AccountUsers'));
const VpnUsers = lazy(() => import('containers/pages/account/VpnUsers'));
const SshKeys = lazy(() => import('containers/pages/servers/SSHKeys/SSHKeys'));
const Compliance = lazy(() => import('containers/pages/account/Compliance'));

const NotFound = lazy(() => import('pages/error/NotFound'));

const PoliciesRedirect = () => {
	const policyLinks = useBrandPolicyLinks();
	window.location.replace(policyLinks.policies);
	return null;
};

const AccountRouter = () => {
	return (
		<AccountLayout>
			<Switch>
				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/users/:username/authentication"
					component={({ match }) => (
						<Redirect
							to={{
								pathname: '/account/users',
								search: `?viewTwoFactor=${match.params.username}`,
							}}
						/>
					)}
				/>

				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/users/:username"
					component={({ match }) => (
						<Redirect
							to={{
								pathname: '/account/users',
								search: `?view=${match.params.username}`,
							}}
						/>
					)}
					whitelistedRoles={[roles.TECHNICIAN]}
				/>

				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/add-user"
					component={() => (
						<Redirect
							to={{
								pathname: '/account/users',
								search: '?inviteUser=true',
							}}
						/>
					)}
				/>

				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/secure-notes"
					component={() => (
						<Redirect
							to={{
								pathname: '/support/mycases',
								search: '?viewSecureNotes=true',
							}}
						/>
					)}
				/>
				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/billing/pay"
					component={() => (
						<Redirect
							to={{
								pathname: '/account/billing',
								search: '?makePayment=true',
							}}
						/>
					)}
				/>

				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/billing/payment-method"
					component={() => <Redirect to="/account/billing" />}
				/>

				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/policies"
					component={PoliciesRedirect}
				/>

				<RoleRestrictedRoute
					path="/account/api-tokens"
					component={APITokens}
					whitelistedRoles={[
						roles.ACCOUNT_OWNER,
						roles.SECONDARY_OWNER,
						roles.PURCHASER,
						roles.SUSPENDED_OWNER,
					]}
				/>

				{/* legacy route */}
				<RoleRestrictedRoute
					path="/account/history"
					component={() => <Redirect to="/account/activity" />}
				/>

				<RoleRestrictedRoute
					path="/account/activity"
					component={Activity}
					whitelistedRoles={[
						roles.ACCOUNT_OWNER,
						roles.SECONDARY_OWNER,
						roles.PURCHASER,
						roles.SUSPENDED_OWNER,
					]}
				/>

				<RoleRestrictedRoute
					path="/account/users"
					component={({ location }) => {
						// legacy hash routes
						if (location.hash === '#vpn') {
							return <Redirect to="/account/cloud-vpn" />;
						}
						if (location.hash === '#api') {
							return <Redirect to="/account/api-tokens" />;
						}

						return <Users />;
					}}
				/>
				<RoleRestrictedRoute path="/account/cloud-vpn" component={VpnUsers} />

				<RoleRestrictedRoute path="/account/ssh-keys" component={SshKeys} />

				<RoleRestrictedRoute
					path="/account/compliance"
					component={Compliance}
				/>

				<RoleRestrictedRoute
					path="/account/preferences"
					component={Preferences}
				/>

				<RoleRestrictedRoute
					path="/account/billing/confirm-one-time-paypal-payment"
					component={ConfirmOneTimePayPalPayment}
					whitelistedRoles={[
						roles.ACCOUNT_OWNER,
						roles.SECONDARY_OWNER,
						roles.SUSPENDED_OWNER,
						roles.PURCHASER,
					]}
				/>

				<RoleRestrictedRoute
					path="/account/billing/transactions"
					component={Transactions}
					whitelistedRoles={[
						roles.ACCOUNT_OWNER,
						roles.SECONDARY_OWNER,
						roles.SUSPENDED_OWNER,
						roles.PURCHASER,
					]}
				/>

				<RoleRestrictedRoute
					path="/account/billing"
					component={BillingOverview}
					whitelistedRoles={[
						roles.ACCOUNT_OWNER,
						roles.SECONDARY_OWNER,
						roles.SUSPENDED_OWNER,
						roles.PURCHASER,
					]}
				/>

				<RoleRestrictedRoute path="/account" component={AccountDetails} />

				<Route component={NotFound} />
			</Switch>
		</AccountLayout>
	);
};

const ACCOUNT_CREATE_PATH = '/account/create';

export { ACCOUNT_CREATE_PATH };

export default AccountRouter;
